import { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage } from "react-intl";
import QuotationRoomsBoards from "app/pages/Booking/Quotation/QuotationRoomsBoards/QuotationRoomsBoards";
import {
	getAccommodationsOfSelectedBoard,
	getBoardsOfSelectedAccommodation,
	getFormattedAccommodations,
	getVisibleBoards,
} from "app/pages/Booking/Quotation/quotationSelectors";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import usePrevious from "app/utils/hooks/usePrevious";
import { HitType, useFlagship } from "@flagship.io/react-sdk";
import { FLAGSHIP_SCREENVIEW_KPIS } from "app/constants";
import { useLocation } from "react-router-dom";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { getStressMarketingLabelForQuotationRoom } from "app/pages/StressMarketing/stressMarketingSelector";
import withRouter from "app/utils/hocs/withRouter";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const QuotationRoomsBoardsContainer = props => {
	const {
		booking = {},
		accommodations = [],
		boardsOfSelectedAccommodation = [],
		accommodationsOfSelectedBoard = [],
		shouldShowBoardsFirst,
		updateBooking,
		boards = [],
	} = props;

	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: false });

	useEffect(() => {
		if (isElementOnScreen) {
			track("quote_view_room");
		}
	}, [isElementOnScreen]);

	const previousBookingBoardCode = usePrevious(booking.board?.code);
	const previousBookingAccommodationcode = usePrevious(booking.accommodation?.code);

	// TODO déplacer l'envoi des ces hits dans QuotationContainer une fois que le composant sera en functional component
	const { sendHits } = useFlagship();
	const { pathname } = useLocation();
	const isSDP = pathname.includes("/sdp");

	useEffect(() => {
		sendHits([
			{
				type: HitType.PAGE_VIEW,
				documentLocation: window.location.origin + window.location.pathname,
			},
			{
				type: HitType.SCREEN_VIEW,
				documentLocation: isSDP
					? FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_SDP_QUOTE
					: FLAGSHIP_SCREENVIEW_KPIS.SCREENVIEW_PP_QUOTE,
			},
		]);
	}, [isSDP]);

	useEffect(() => {
		let updatedBooking = {};

		// TODO delete .codes which is replaced by .boards
		const bookingCodes = booking.accommodation.boards || booking.accommodation.codes;

		// cas ou on choisit la chambre en premier
		if (!shouldShowBoardsFirst) {
			// Initialisation des blocs aux valeurs (inclus) par défaut si aucune sélection du user a été faite
			if (!booking.accommodation.code && accommodations[0]) {
				updatedBooking.accommodation = accommodations[0];
			} else if (
				// If not shouldShowBoardsFirst, if the user change the accommodation, we reset the user board selection
				bookingCodes &&
				previousBookingAccommodationcode &&
				previousBookingAccommodationcode !== booking.accommodation.code
			) {
				updatedBooking.board = boardsOfSelectedAccommodation[0];
			}

			if (!booking.board.code && boardsOfSelectedAccommodation[0]) {
				updatedBooking.board = boardsOfSelectedAccommodation[0];
			}
		} else {
			if (!booking.board.code && boards[0]) {
				updatedBooking.board = boards[0];
			} else if (
				previousBookingBoardCode &&
				previousBookingBoardCode !== booking.board.code
			) {
				// if shouldShowBoardsFirst, if the user change the board, we reset the user accommodation selection
				updatedBooking.accommodation = accommodationsOfSelectedBoard[0];
			}

			if (!booking.accommodation.code && accommodationsOfSelectedBoard[0]) {
				updatedBooking.accommodation = accommodationsOfSelectedBoard[0];
			}
		}

		if (!isEmpty(updatedBooking)) {
			updateBooking(updatedBooking);
		}
	}, [
		shouldShowBoardsFirst,
		booking.accommodation.code,
		booking.board.code,
		booking.accommodation.boards,
		booking.accommodation.codes,
		previousBookingBoardCode,
		previousBookingAccommodationcode,
	]);

	const onBoardSelection = useCallback(board => {
		updateBooking({
			board: board,
		});
	}, []);

	const onAccommodationSelection = useCallback(accommodation => {
		updateBooking({
			accommodation: accommodation,
		});
	}, []);

	const headerTitle = (
		<FormattedMessage
			id={
				shouldShowBoardsFirst
					? "quotation.board.and.accommodation.title"
					: "quotation.accommodation.and.board.title"
			}
		>
			{message => {
				return (
					<div className="quotation__headline">
						<Typography
							variant={TYPOGRAPHY_VARIANTS.XL3}
							variantMD={TYPOGRAPHY_VARIANTS.XL4}
							isBold
							className="quotation__title"
							component="h2"
						>
							{message}
						</Typography>
					</div>
				);
			}}
		</FormattedMessage>
	);

	return (
		<div className="quotation__item" ref={ref}>
			{headerTitle}
			<div className="quotation__content">
				<QuotationRoomsBoards
					{...props}
					onBoardChange={onBoardSelection}
					onAccommodationChange={onAccommodationSelection}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = state => {
	let booking = state.booking || {};

	const stressMarketingMsg = getStressMarketingLabelForQuotationRoom(booking?.departureDate)(
		state
	);
	return {
		booking: booking,
		shouldShowBoardsFirst: state.quotation.showBoardsFirst,
		accommodations: getFormattedAccommodations(state),
		accommodationsOfSelectedBoard: getAccommodationsOfSelectedBoard(state),
		boards: getVisibleBoards(state),
		boardsOfSelectedAccommodation: getBoardsOfSelectedAccommodation(state),
		stressMarketingLabel: stressMarketingMsg,
	};
};

QuotationRoomsBoardsContainer.propTypes = {
	shouldShowBoardsFirst: PropTypes.bool,
	boards: PropTypes.arrayOf(PropTypes.any),
	accommodationsOfSelectedBoard: PropTypes.arrayOf(PropTypes.any),
	booking: PropTypes.any,
	accommodations: PropTypes.any,
	boardsOfSelectedAccommodation: PropTypes.any,
	updateBooking: PropTypes.func,
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			updateBooking,
		},
		dispatch
	);
};

export default withRouter(
	connect(
		mapStateToProps,
		mapDispatchToProps
	)(QuotationRoomsBoardsContainer)
);
